import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns';
import { CalendarEvent, CalendarTag, CalendarEventWithTags } from '../types/calendar';
import { calendarApiService } from '../services/calendarApi';
import { CreateCalendarEventModal } from '../components/CreateCalendarEventModal';
import { CreateCalendarTagModal } from '../components/CreateCalendarTagModal';
import { CalendarDetailModal } from '../components/CalendarDetailModal';
import { CalendarView } from '../components/CalendarView';
import { Plus, Loader, AlertCircle, Search, Calendar as CalendarIcon, Tag, TagIcon, List, Grid, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { AnimatePresence } from 'framer-motion';
import { cn } from '../utils/cn';
import { getLimitedTesseractTeamAccess } from '../utils/tesseractTeamAccess';
import { PageHeader } from '../components/ui/PageHeader';

// The calendar only ever renders one month at a time, so events are fetched a
// month at a time and cached. One month either side is pre-loaded so paging
// backwards or forwards is instant and does not hit the network.
const MONTH_PREFETCH_RADIUS = 1;

const monthKey = (date: Date) => format(date, 'yyyy-MM');

/** Month keys that should be resident for a given visible month. */
const monthsAround = (date: Date): string[] => {
  const keys: string[] = [];
  for (let offset = -MONTH_PREFETCH_RADIUS; offset <= MONTH_PREFETCH_RADIUS; offset++) {
    keys.push(monthKey(addMonths(date, offset)));
  }
  return keys;
};

const monthKeyToDate = (key: string) => new Date(`${key}-01T00:00:00`);

export const CalendarListView = () => {
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const [events, setEvents] = useState<CalendarEventWithTags[]>([]);
  const [tags, setTags] = useState<CalendarTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState(false);
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => new Date());

  // Which month windows are already in `events`, so a revisit is free. Held in a
  // ref because loading a window must not re-trigger the effect that loads it.
  const loadedMonthsRef = useRef<Set<string>>(new Set());
  // True once an unbounded fetch has run, which makes every month resident.
  const [allEventsLoaded, setAllEventsLoaded] = useState(false);
  // Bumped to invalidate every cached window after a create, edit or delete.
  const [reloadToken, setReloadToken] = useState(0);

  useDocumentTitle('Calendar');

  const debouncedSearch = useDebounce(searchQuery, 300);

  const limitedTesseract = useMemo(
    () => getLimitedTesseractTeamAccess(identity, isDev),
    [identity, isDev],
  );

  const canCreateEvents = useMemo(() => {
    if (!identity) return false;
    if (limitedTesseract?.calendarViewOnly) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.create');
  }, [identity, limitedTesseract]);

  const canCreateTags = useMemo(() => {
    if (!identity) return false;
    if (limitedTesseract?.calendarViewOnly) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.tags.create');
  }, [identity, limitedTesseract]);

  const canView = useMemo(() => {
    if (!identity) return false;
    if (limitedTesseract) return true;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.view');
  }, [identity, limitedTesseract]);

  // Drop every cached window and refetch. Used after a create, edit or delete,
  // and whenever the status filter changes the server-side result set.
  const invalidate = useCallback(() => {
    loadedMonthsRef.current = new Set();
    setAllEventsLoaded(false);
    setEvents([]);
    setReloadToken(token => token + 1);
  }, []);

  // The status filter is applied by the backend, so cached windows no longer
  // match it. Skipped on mount, where there is nothing cached to throw away and
  // invalidating would just fire a second identical fetch.
  const statusFilterMounted = useRef(false);
  useEffect(() => {
    if (!statusFilterMounted.current) {
      statusFilterMounted.current = true;
      return;
    }
    invalidate();
  }, [statusFilter, invalidate]);

  useEffect(() => {
    let cancelled = false;
    calendarApiService
      .getAllTags()
      .then(tagsData => {
        if (!cancelled) setTags(tagsData || []);
      })
      .catch(() => {
        if (!cancelled) setTags([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  // Merge a fetched window into the cache, keeping one entry per event id.
  const mergeEvents = useCallback((incoming: CalendarEventWithTags[]) => {
    setEvents(previous => {
      const byId = new Map(previous.map(event => [event.id, event]));
      for (const event of incoming) byId.set(event.id, event);
      return Array.from(byId.values());
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // An unbounded fetch has already covered every month.
      if (allEventsLoaded) {
        setLoading(false);
        setFetchingMore(false);
        return;
      }

      // The list view searches across everything, so it needs the full set. The
      // calendar view only needs the months it can actually display.
      const wantsEverything = viewMode === 'list';
      const missingMonths = wantsEverything
        ? []
        : monthsAround(visibleMonth).filter(key => !loadedMonthsRef.current.has(key));

      if (!wantsEverything && missingMonths.length === 0) {
        // Clears any indicator left over from a request abandoned by fast paging.
        setLoading(false);
        setFetchingMore(false);
        return;
      }

      // Show the blocking spinner only when there is nothing on screen yet;
      // paging to a new month refreshes in the background instead of blanking.
      const isFirstLoad = events.length === 0;
      if (isFirstLoad) setLoading(true);
      else setFetchingMore(true);
      setError(null);

      try {
        const params: { start_time?: number; end_time?: number; status?: string } = {};
        if (statusFilter) params.status = statusFilter;
        if (!wantsEverything) {
          // One request spans all the missing months. It may re-fetch an
          // already-loaded month in between, which merges away harmlessly.
          const sorted = [...missingMonths].sort();
          params.start_time = startOfMonth(monthKeyToDate(sorted[0])).getTime();
          params.end_time = endOfMonth(monthKeyToDate(sorted[sorted.length - 1])).getTime();
        }

        const eventsData = await calendarApiService.getEventsWithTags(params);
        if (cancelled) return;

        mergeEvents(eventsData || []);
        if (wantsEverything) setAllEventsLoaded(true);
        else for (const key of missingMonths) loadedMonthsRef.current.add(key);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load calendar data');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetchingMore(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // `events` is deliberately excluded: merging a window must not re-run the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, monthKey(visibleMonth), statusFilter, reloadToken, allEventsLoaded, mergeEvents]);

  const filteredEvents = useMemo(() => {
    if (!events || events.length === 0) return [];
    
    let filtered = events;
    
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(query) ||
        event.description.toLowerCase().includes(query) ||
        event.id.toLowerCase().includes(query)
      );
    }

    if (tagFilter) {
      filtered = filtered.filter(event => event.tags?.some(tag => tag.id === tagFilter));
    }
    
    return [...filtered].sort((a, b) => a.start_time - b.start_time);
  }, [events, debouncedSearch, tagFilter]);

  const STATUS_OPTIONS = ['active', 'cancelled', 'postponed'];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/15 text-green-300';
      case 'cancelled': return 'bg-red-500/15 text-red-300';
      case 'postponed': return 'bg-yellow-500/15 text-yellow-300';
      default: return 'bg-gray-500/15 text-gray-300';
    }
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEventId(event.id);
  };

  const handleGenerateICS = async () => {
    if (!canView) return;
    
    try {
      const params: {
        start_time?: number;
        end_time?: number;
        status?: string;
      } = {};
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      await calendarApiService.generateICS(params);
      alert('Calendar ICS feed has been generated and published to KV store.');
    } catch (err) {
      alert('Failed to generate ICS feed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Planning"
        title="Calendar"
        description="Schedule and manage events"
        icon={CalendarIcon}
        actions={
          <>
            {canView && (
              <button onClick={handleGenerateICS} className="glass-button-secondary" title="Generate and publish ICS feed to KV store">
                <Download className="w-4 h-4" />
                Generate ICS
              </button>
            )}
            {canCreateTags && (
              <button onClick={() => setIsCreateTagModalOpen(true)} className="glass-button-secondary">
                <Tag className="w-4 h-4" />
                Create Tag
              </button>
            )}
            {canCreateEvents && (
              <button onClick={() => setIsCreateEventModalOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4" />
                Create Event
              </button>
            )}
          </>
        }
      />

      {/* Toolbar */}
      <div className="glass-card p-3 md:p-4">

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="segmented">
            <button
              onClick={() => setViewMode('calendar')}
              className="segmented-item" data-active={viewMode === 'calendar' ? 'true' : 'false'}
            >
              <Grid className="w-4 h-4" />
              <span>Calendar</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className="segmented-item" data-active={viewMode === 'list' ? 'true' : 'false'}
            >
              <List className="w-4 h-4" />
              <span>List</span>
            </button>
          </div>
        </div>

        {/* Filters - only show in list view */}
        {viewMode === 'list' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="glass-input w-full pl-10"
                placeholder="Search events..."
              />
            </div>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="glass-input w-full pl-10"
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400" />
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="glass-input w-full pl-10"
              >
                <option value="">All Tags</option>
                {tags.map(tag => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="glass-card p-4 border-l-4 border-red-400">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-accent-400" />
        </div>
      )}

      {/* Calendar View */}
      {!loading && viewMode === 'calendar' && (
        <CalendarView
          events={events}
          tags={tags}
          onEventClick={handleEventClick}
          onCreateEvent={canCreateEvents ? () => setIsCreateEventModalOpen(true) : undefined}
          visibleMonth={visibleMonth}
          onVisibleMonthChange={setVisibleMonth}
          isFetching={fetchingMore}
        />
      )}

      {/* List View */}
      {!loading && viewMode === 'list' && !allEventsLoaded && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-accent-400" />
        </div>
      )}

      {!loading && viewMode === 'list' && allEventsLoaded && (
        <div className="space-y-4">
          <AnimatePresence>
            {filteredEvents.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <CalendarIcon className="w-12 h-12 text-glass-400 mx-auto mb-4" />
                <p className="text-glass-400">
                  {searchQuery || statusFilter ? 'No events match your filters' : 'No events found'}
                </p>
              </div>
            ) : (
              filteredEvents.map((event) => (
                <div
                  key={event.id}
                  onClick={() => setSelectedEventId(event.id)}
                  className="glass-card p-6 cursor-pointer hover:bg-glass-200/10 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2">{event.title}</h3>
                      <div className="flex items-center space-x-4 text-sm text-glass-400 mb-3">
                        <span className="font-mono">
                          {new Date(event.start_time).toLocaleString()}
                        </span>
                        {event.end_time && (
                          <span className="font-mono">
                            → {new Date(event.end_time).toLocaleString()}
                          </span>
                        )}
                        <span>ID: <span className="font-mono">{event.id}</span></span>
                      </div>
                      <p className="text-glass-300 line-clamp-2">{event.description}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <span className={cn(
                        "text-xs font-bold px-3 py-1 rounded-full",
                        getStatusColor(event.status)
                      )}>
                        {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Create Event Modal */}
      {isCreateEventModalOpen && (
        <CreateCalendarEventModal
          isOpen={isCreateEventModalOpen}
          onClose={() => setIsCreateEventModalOpen(false)}
          onSuccess={() => {
            setIsCreateEventModalOpen(false);
            invalidate();
          }}
        />
      )}

      {/* Create Tag Modal */}
      {isCreateTagModalOpen && (
        <CreateCalendarTagModal
          isOpen={isCreateTagModalOpen}
          onClose={() => setIsCreateTagModalOpen(false)}
          onSuccess={() => {
            setIsCreateTagModalOpen(false);
            invalidate();
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedEventId && (
        <CalendarDetailModal
          eventId={selectedEventId}
          isOpen={!!selectedEventId}
          onClose={() => setSelectedEventId(null)}
          onUpdate={invalidate}
        />
      )}
    </div>
  );
};

